import React, { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField, Stack, MenuItem } from "@mui/material";
import { togglePreQuoteEditDialog } from "../../../../../app/slices/VerificationModuleSlice";
import { useUpdatePreQuoteMutation } from "../../../../../services/verifyService/verify-api";
import { PreQuoteUpdateCommand } from "../../../../../services/verifyService/commands";
import { usePayerListQuery } from "../../../../../services/orgService/org-api";
import { Payer } from "../../../../../services/orgService/model/payer/Payer";

function PreQuoteEditDialog(){
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector((state) => state.verificationModule.PreQuoteEditDialog);
    const verify = useAppSelector((state) => state.verification.verification);
    const [updatePreQuote] = useUpdatePreQuoteMutation();
    const { data: payers } = usePayerListQuery(verify.center);
    const sortedPayers = useMemo(
        () => payers ? [...payers].sort((a, b) => a.name.localeCompare(b.name)) : [],
        [payers]
    );
    const [notify, setNotify] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [patientName, setPatientName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [admissionType, setAdmissionType] = useState('');
    const [serviceDate, setServiceDate] = useState('');
    const [selectedPayer, setSelectedPayer] = useState<Payer | null>(null);
    const [policyNumber, setPolicyNumber] = useState('');
    const [groupNumber, setGroupNumber] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setPatientName(`${verify.lastName}, ${verify.firstName}`);
        setBirthDate(verify.birthDate ? dayjs(verify.birthDate).format('YYYY-MM-DD') : '');
        setServiceDate(verify.serviceDate ? dayjs(verify.serviceDate).format('YYYY-MM-DD') : '');
        setAdmissionType(verify.admissionType ?? '');
        setPhoneNumber(verify.preQuoteAttributes.phoneNumber ?? '');
        setPolicyNumber(verify.preQuoteAttributes.policyNumber ?? '');
        setGroupNumber(verify.preQuoteAttributes.groupNumber ?? '');
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve payer separately — sortedPayers may not be populated yet when the dialog first opens
    useEffect(() => {
        if (!isOpen || sortedPayers.length === 0) return;
        setSelectedPayer(sortedPayers.find(p => p.code === verify.primaryPayerId) ?? null);
    }, [isOpen, sortedPayers]); // eslint-disable-line react-hooks/exhaustive-deps

    const isValid = !!patientName.trim() && !!birthDate && !!admissionType && !!serviceDate && !!selectedPayer;

    const handleClose = () => {
        dispatch(togglePreQuoteEditDialog());
    };

    const handleSubmit = async () => {
        if (!isValid) return;
        try {
            setSubmitting(true);
            const formatDate = (dateString: string | undefined): string | undefined => {
                if (!dateString) return undefined;
                return dateString + 'T00:00:00';
            };

            const command: PreQuoteUpdateCommand = {
                verificationId: verify.id!,
                patientName: patientName.trim(),
                phoneNumber: phoneNumber.trim() || undefined,
                birthDate: formatDate(birthDate),
                admissionType: admissionType || undefined,
                serviceDate: formatDate(serviceDate),
                primaryPayerCode: selectedPayer?.code || undefined,
                primaryPayerName: selectedPayer?.name || undefined,
                policyNumber: policyNumber.trim() || undefined,
                groupNumber: groupNumber.trim() || undefined,
            };
            await updatePreQuote(command).unwrap();
            dispatch(togglePreQuoteEditDialog());
        } catch (error) {
            setNotify(true);
        } finally {
            setSubmitting(false);
        }
    };

    return <React.Fragment>
        <Dialog open={isOpen} maxWidth="sm" fullWidth>
            <DialogTitle sx={{color: '#9C27B0'}}>Edit Pre-Quote</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    <TextField
                        label="Patient Name"
                        value={patientName}
                        onChange={(e) => setPatientName(e.target.value)}
                        required
                        fullWidth
                        size="small"
                        placeholder="Last, First"
                    />
                    <TextField
                        label="Phone Number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="(555) 123-4567"
                    />
                    <TextField
                        label="Birth Date"
                        type="date"
                        value={birthDate}
                        onChange={(e) => setBirthDate(e.target.value)}
                        required
                        fullWidth
                        size="small"
                        InputLabelProps={{shrink: true}}
                    />
                    <TextField
                        label="Admission Type"
                        value={admissionType}
                        onChange={(e) => setAdmissionType(e.target.value)}
                        select
                        required
                        fullWidth
                        size="small"
                    >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="OP">Outpatient</MenuItem>
                        <MenuItem value="IP">Inpatient</MenuItem>
                    </TextField>
                    <TextField
                        label="Service Date"
                        type="date"
                        value={serviceDate}
                        onChange={(e) => setServiceDate(e.target.value)}
                        required
                        fullWidth
                        size="small"
                        InputLabelProps={{shrink: true}}
                    />
                    <Autocomplete
                        options={sortedPayers}
                        getOptionLabel={(option) => option.name}
                        value={selectedPayer}
                        onChange={(_, value) => setSelectedPayer(value)}
                        renderInput={(params) => (
                            <TextField {...params} label="Primary Payer" size="small" required />
                        )}
                        renderOption={(props, option, { index }) => (
                            <li {...props} key={`${option.code}-${index}`}>{option.name}</li>
                        )}
                        fullWidth
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                    />
                    <TextField
                        label="Policy Number"
                        value={policyNumber}
                        onChange={(e) => setPolicyNumber(e.target.value)}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Group Number"
                        value={groupNumber}
                        onChange={(e) => setGroupNumber(e.target.value)}
                        fullWidth
                        size="small"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color='secondary'>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    disabled={!isValid || submitting}
                    sx={{color: '#9C27B0'}}
                >
                    {submitting ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
        <Snackbar open={notify} onClose={() => setNotify(false)} autoHideDuration={6000}>
            <Alert severity='error'>Failed to update pre-quote. Please try again.</Alert>
        </Snackbar>
    </React.Fragment>
}

export {PreQuoteEditDialog}
